<template>
  <PageWrapper>
    <div class="container">
      <ProductsHeader :category="category ?? null" />

      <ProductsCatalogGrid
        :is-loaded="isLoaded"
        :products="filteredProducts"
      />
      <div
        v-if="isLoaded && filteredProducts.length === 0"
        class="text-center"
      >
        {{ t('no_product_meets_selected_filtration') }}
      </div>
      <BaseCategoriesSection
        v-if="isLoaded"
        :title="t('categories_title')"
      />
    </div>
  </PageWrapper>
  <BaseModal modal-id="product-detail-modal">
    <ModalProductDetail />
  </BaseModal>
  <BaseModal
    :title="t('watchdog_title')"
    modal-id="modal-watchdog"
  >
    <ModalWatchdog />
  </BaseModal>
</template>

<script setup>
import { useRouteQuery } from '@vueuse/router'
import { storeToRefs } from 'pinia'
import { computed, ref, watch, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import useHead from '@/mixins/headManager'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import PageWrapper from '@/components/general/BasePageWrapper.vue'
import ProductsCatalogGrid from '@/components/products/ProductsCatalogGrid.vue'
import ProductsHeader from '@/components/products/ProductsHeader.vue'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import BaseCategoriesSection from "@/components/general/BaseCategoriesSection.vue";
import { removeValueFromObject } from '@/mixins/utils'
import { useAppStore } from '@/stores/AppStore'
import { useNavbarStore } from '@/stores/NavbarStore'
import { useProductStore } from '@/stores/ProductStore'

const BaseModal = defineAsyncComponent(() => import('@/components/general/BaseModal.vue'))
const ModalProductDetail = defineAsyncComponent(() => import('@/components/modals/ModalProductDetail.vue'))
const ModalWatchdog = defineAsyncComponent(() => import('@/components/modals/ModalWatchdog.vue'))

const { t } = useI18n()

const route = useRoute()
const isLoaded = ref(false)
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path

const products = ref([])
const category = ref()
const kind = ref()
const categoryTitle = ref()

// Inits navbars
const { setNavbar } = useNavbarStore()

setNavbar({
  backgroundColor: 'bg-primary-700',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: url,
    },
  ],
})

const fetchData = async () => {
  isLoaded.value = false
  const { data } = await ApiServiceV1.get(`products?permalink=${route.params.permalink}`)
  category.value = data.category
  products.value = data.products
  kind.value = data.category.systemName
  categoryTitle.value = data.category.title
  isLoaded.value = true
  route.meta.pageReady = true
  GtmEventEmitter.viewItemList({
    category: data.category,
    products: data.products,
  })

  useHead({
  title: category.value.ogTitle || category.value.title,
  meta: [
    {
      property: `og:title`,
      content: category.value.ogTitle || category.value.title,
    },
    {
      property: `og:description`,
      content: category.value.ogDescription || category.value.description,
    },
  ],
})
}

const productStore = useProductStore()
const { filterCount, isFavoritesInFilter, getFilters, filters } = storeToRefs(productStore)
const appStore = useAppStore()

const { getFavoriteProducts } = storeToRefs(appStore)

const favoritesProducts = computed(() =>
  products.value.filter((product) => getFavoriteProducts.value.includes(product.id)),
)
const cleanProducts = computed(() => (isFavoritesInFilter.value ? favoritesProducts.value : products.value))
const cleanFilters = computed(() => removeValueFromObject(getFilters.value, 'favorites')) // "favorites" needs to be removed from filters and handled little differently

const filteredProducts = computed(() => {
  return cleanProducts.value.filter((product) =>
    cleanFilters.value.every((f) => product.properties.some((property) => property.systemName === f)),
  )
})

const urlFilter = useRouteQuery('filter')
if (urlFilter.value) {
  productStore.setFilter(urlFilter.value.split(' '))
}

watch(
  () => filterCount.value,
  () => {
    urlFilter.value = filters.value.join(' ')
  },
)

watch(
  () => route.params.permalink,

  async () => {
    productStore.clearFilters()
    fetchData()
  },
  { immediate: true },
)
</script>

<i18n>
  {
    "cs": {
    "categories_title": "Nevybrali jste si? Máme toho více!",
    "no_product_meets_selected_filtration": "Žádný produkt nevyhovuje zvolené filtraci",
    "watchdog_title": "Chcete si počkat?"
},
    "sk": {
    "categories_title": "Nevybrali ste si? Máme toho viac!",
    "no_product_meets_selected_filtration": "Žiadny produkt nevyhovuje zvolenej filtrácii",
    "watchdog_title": "Chcete si počkať?"
},
    "pl": {
    "categories_title": "Nie wiesz co wybrać? Zobacz więcej!",
    "no_product_meets_selected_filtration": "Żaden z produktów nie odpowiada zaznaczonym właściwościom",
    "watchdog_title": "Chcesz poczekać?"
},
    "hu": {
    "categories_title": "Nem választottál? Van még több is!",
    "no_product_meets_selected_filtration": "A szűrő beállításainak egyetlen termék sem felel meg",
    "watchdog_title": "Szeretnél várni?"
},
    "de": {
    "categories_title": "Noch nicht das Richtige gefunden? Wir haben mehr! ",
    "no_product_meets_selected_filtration": "Kein Produkt entspricht dem ausgewählten Filter",
    "watchdog_title": "Möchtest du warten?"
},
    "at": {
    "categories_title": "Noch nicht das Richtige gefunden? Wir haben mehr! ",
    "no_product_meets_selected_filtration": "Kein Produkt entspricht dem ausgewählten Filter",
    "watchdog_title": "Möchtest du warten?"
},
    "ro": {
    "categories_title": "Nu v-ați hotărât? Avem mai multe!",
    "no_product_meets_selected_filtration": "Niciun produs nu se potrivește cu filtrul selectat",
    "watchdog_title": "Dorești să aștepți?"
},
    "fr": {
    "categories_title": "Vous n’avez pas trouvé votre bonheur ? On en a encore plein d’autres !",
    "no_product_meets_selected_filtration": "Aucun produit ne correspond aux filtres sélectionnés",
    "watchdog_title": "Voulez-vous patienter ?"
}
  }
</i18n>
